import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  listDoorsForSchool,
  createDoor,
  deleteDoor,
  fetchNvrChannels,
  fetchDeviceSnapshotBlob,
} from '../../api/hikvision'
import { fetchFirePanelStatus } from '../../api/fire'
import AddHikvisionDeviceModal from '../AddHikvisionDeviceModal'
import { FIRE_PANEL_STATE } from '../FirePanelStatusIcon'
import { firePanelDetailText } from '../../lib/firePanelCopy'
import { lastPollLine } from '../../lib/lastPollLabel'
import HikvisionLiveVideo from './HikvisionLiveVideo'
import {
  AddDeviceLink,
  availabilityAccent,
  DeviceRowCard,
  EquipmentEmpty,
  GhostLink,
  IconAction,
  NavPageLink,
  SectionFrame,
  StatusDot,
  schoolSurface,
} from './schoolEquipmentChrome'

const isAdminOrEngineer = (role) => {
  const r = typeof role === 'string' ? role.trim().toLowerCase() : ''
  return r === 'admin' || r === 'engineer'
}

const connectivity = (nvr) => {
  if (nvr.enabled === false) return { tone: 'muted', label: 'Опрос выключен' }
  if (nvr.status === true) return { tone: 'ok', label: 'Доступен' }
  if (nvr.status === false) return { tone: 'critical', label: 'Недоступен' }
  return { tone: 'muted', label: 'Нет данных' }
}

const ChannelStatusBits = ({ channel, compact = false }) => {
  const online = channel.online
  const recording = channel.recording
  const cls = compact ? 'text-[9px]' : 'text-[10px]'
  return (
    <div className={`flex shrink-0 items-center gap-2 font-medium uppercase tracking-wide ${cls}`}>
      <span className={online === true ? 'text-positive' : online === false ? 'text-critical' : 'text-muted-500'}>
        {online === true ? 'связь' : online === false ? 'нет связи' : '—'}
      </span>
      <span className={recording === true ? 'text-brand-200' : recording === false ? 'text-warning' : 'text-muted-500'}>
        {recording === true ? 'запись' : recording === false ? 'не пишется' : '—'}
      </span>
    </div>
  )
}

const ChannelRow = ({ channel, selected, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(channel)}
    className={`flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition ${
      selected
        ? 'bg-brand-400/15 text-white'
        : `${schoolSurface.tile} text-muted-200 hover:bg-white/[0.05]`
    }`}
  >
    <div className="min-w-0">
      <p className="truncate font-medium">{channel.name || `Канал ${channel.channel}`}</p>
      <p className="mt-0.5 font-mono text-[11px] tabular-nums text-muted-500">канал {channel.channel}</p>
    </div>
    <ChannelStatusBits channel={channel} />
  </button>
)

const ChannelTile = ({ channel, selected, onSelect, deviceId }) => {
  const rootRef = useRef(null)
  const [visible, setVisible] = useState(false)
  const [posterUrl, setPosterUrl] = useState(null)
  const [posterFailed, setPosterFailed] = useState(false)

  useEffect(() => {
    const el = rootRef.current
    if (!el) return undefined
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin: '120px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!visible || !deviceId || !channel?.channel) return undefined
    let cancelled = false
    let objectUrl = null

    fetchDeviceSnapshotBlob(deviceId, String(channel.channel), { profile: 'sub' })
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url)
          return
        }
        objectUrl = url
        setPosterUrl(url)
        setPosterFailed(false)
      })
      .catch(() => {
        if (!cancelled) setPosterFailed(true)
      })

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [visible, deviceId, channel?.channel])

  return (
    <button
      ref={rootRef}
      type="button"
      onClick={() => onSelect(channel)}
      className={`group relative flex aspect-[5/4] flex-col overflow-hidden rounded-xl text-left transition ${
        selected ? 'ring-1 ring-brand-400/50' : 'hover:bg-white/[0.03]'
      }`}
    >
      <div className="absolute inset-0 bg-[#0d111c]">
        {posterUrl ? (
          <img src={posterUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            {posterFailed ? (
              <span className="material-icons text-2xl text-muted-600">videocam_off</span>
            ) : (
              <span className="material-icons animate-pulse text-2xl text-muted-500">videocam</span>
            )}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
      </div>

      <div className="relative mt-auto flex min-w-0 flex-col gap-1 p-2.5">
        <p
          className="line-clamp-2 text-xs font-medium leading-snug text-white"
          title={channel.name || `Канал ${channel.channel}`}
        >
          {channel.name || `Канал ${channel.channel}`}
        </p>
        <div className="flex items-center justify-between gap-1">
          <span className="font-mono text-[10px] tabular-nums text-muted-400">#{channel.channel}</span>
          <ChannelStatusBits channel={channel} compact />
        </div>
      </div>
    </button>
  )
}

const ViewModeToggle = ({ mode, onChange }) => (
  <div className={`inline-flex rounded-xl ${schoolSurface.tile} p-0.5`}>
    <button
      type="button"
      onClick={() => onChange('list')}
      className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium transition ${
        mode === 'list' ? 'bg-white/[0.06] text-white' : 'text-muted-400 hover:text-white'
      }`}
      title="Список"
    >
      <span className="material-icons text-sm">view_list</span>
      Список
    </button>
    <button
      type="button"
      onClick={() => onChange('grid')}
      className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium transition ${
        mode === 'grid' ? 'bg-white/[0.06] text-white' : 'text-muted-400 hover:text-white'
      }`}
      title="Плитки"
    >
      <span className="material-icons text-sm">grid_view</span>
      Плитки
    </button>
  </div>
)

const fireStatusMeta = (state, nvrUnavailable, t) => {
  if (nvrUnavailable || state === FIRE_PANEL_STATE.UNAVAILABLE) {
    return { tone: 'muted', label: t('fire.panel.state.unavailable') }
  }
  if (state === FIRE_PANEL_STATE.ON_FIRE) {
    return { tone: 'critical', label: t('fire.panel.state.on_fire') }
  }
  if (state === FIRE_PANEL_STATE.FAULT) {
    return { tone: 'warning', label: t('fire.panel.state.fault') }
  }
  if (state === FIRE_PANEL_STATE.OK) {
    return { tone: 'ok', label: t('fire.panel.state.ok') }
  }
  return { tone: 'muted', label: t('fire.panel.state.not_connected') }
}

const FireRow = ({ objectIp, nvr, nvrUnavailable, canManage = false }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [status, setStatus] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!objectIp) return undefined
    let cancelled = false

    const load = async () => {
      try {
        const data = await fetchFirePanelStatus({ schoolIp: objectIp })
        if (!cancelled) {
          setStatus(data)
          setError(null)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.message || err?.message || t('fire.panel.loadError'))
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    setIsLoading(true)
    load()
    const interval = setInterval(load, 60 * 1000)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [objectIp, t])

  const state = nvrUnavailable
    ? FIRE_PANEL_STATE.UNAVAILABLE
    : status?.state || FIRE_PANEL_STATE.NOT_CONNECTED
  const onFire = state === FIRE_PANEL_STATE.ON_FIRE
  const meta = fireStatusMeta(state, nvrUnavailable, t)
  const detail = nvrUnavailable
    ? t('fire.panel.nestedUnavailableBecauseNvr')
    : error || firePanelDetailText(status, t)

  if (isLoading) {
    return (
      <DeviceRowCard
        icon="local_fire_department"
        iconClass="text-muted-500"
        name={t('fire.panel.sectionTitle')}
        address="порты 1/16"
        status={<StatusDot tone="muted" label={t('common.loading')} />}
        reserveActions={canManage}
      />
    )
  }

  return (
    <DeviceRowCard
      icon="local_fire_department"
      iconClass={
        onFire
          ? 'text-critical'
          : state === FIRE_PANEL_STATE.FAULT
            ? 'text-warning'
            : state === FIRE_PANEL_STATE.OK
              ? 'text-positive'
              : 'text-muted-500'
      }
      name={t('fire.panel.sectionTitle')}
      address="порты 1/16"
      extra={nvr?.name || null}
      status={<StatusDot tone={meta.tone} label={meta.label} />}
      accent={availabilityAccent(meta.tone)}
      reserveActions={canManage}
      onOpen={() => navigate(`/schools/${encodeURIComponent(objectIp)}/nvr/${nvr.id}`)}
    >
      {(nvrUnavailable || onFire || state === FIRE_PANEL_STATE.FAULT || error) && detail && (
        <p className="mt-2 pl-12 text-xs text-muted-500">{detail}</p>
      )}
    </DeviceRowCard>
  )
}

const NvrCard = ({ nvr, objectIp, canManage, onDelete, deletingId }) => {
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState(false)
  const [channels, setChannels] = useState([])
  const [channelsLoading, setChannelsLoading] = useState(false)
  const [channelsError, setChannelsError] = useState(null)
  const [selectedChannel, setSelectedChannel] = useState(null)
  const [profile, setProfile] = useState('sub')
  const [viewMode, setViewMode] = useState('list')
  const [channelQuery, setChannelQuery] = useState('')

  const loadChannels = async () => {
    setChannelsLoading(true)
    setChannelsError(null)
    try {
      const data = await fetchNvrChannels(nvr.id)
      const list = Array.isArray(data?.channels) ? data.channels : []
      setChannels(list)
      setSelectedChannel((prev) => {
        if (prev && list.some((c) => String(c.channel) === String(prev.channel))) return prev
        return list[0] || null
      })
    } catch (err) {
      setChannelsError(err?.response?.data?.message || err?.message || 'Не удалось загрузить каналы')
    } finally {
      setChannelsLoading(false)
    }
  }

  useEffect(() => {
    loadChannels()
    const interval = setInterval(loadChannels, 5 * 60 * 1000)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nvr.id])

  const filteredChannels = useMemo(() => {
    const q = channelQuery.trim().toLowerCase()
    if (!q) return channels
    return channels.filter((ch) => {
      const name = String(ch.name || '').toLowerCase()
      const id = String(ch.channel || '')
      return name.includes(q) || id.includes(q)
    })
  }, [channels, channelQuery])

  const state = connectivity(nvr)
  const nvrDown = nvr.enabled !== false && nvr.status === false
  const showChannelStats = nvr.status === true && channels.length > 0
  const onlineCount = channels.filter((c) => c.online === true).length
  const recordingCount = channels.filter((c) => c.recording === true).length
  const poll = nvrDown ? lastPollLine(nvr.scrapedAt) : null

  return (
    <DeviceRowCard
      icon="video_camera_front"
      name={nvr.name || `#${nvr.id}`}
      address={`${nvr.host}:${nvr.port}`}
      extra={[nvr.model, poll].filter(Boolean).join(' · ') || null}
      status={<StatusDot tone={state.tone} label={state.label} />}
      accent={availabilityAccent(state.tone)}
      onOpen={() => navigate(`/schools/${encodeURIComponent(objectIp)}/nvr/${nvr.id}`)}
      hoverActions={
        canManage ? (
          <IconAction
            title="Удалить"
            onClick={() => onDelete(nvr.id)}
            busy={deletingId === nvr.id}
            icon="delete"
            tone="critical"
          />
        ) : null
      }
    >
      {showChannelStats ? (
        <p className="mt-2 pl-12 text-xs text-muted-500">
          {channels.length} каналов · {onlineCount} на связи · {recordingCount} под записью
        </p>
      ) : null}

      <div data-row-action className="mt-2 pl-12">
        <GhostLink className="text-xs" onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'Скрыть каналы' : 'Каналы и видео'}
        </GhostLink>
      </div>

      {expanded && (
        <div
          data-row-action
          className="relative mt-3 space-y-4 pt-3"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative min-w-0 flex-1">
              <span className="material-icons pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-muted-500">
                search
              </span>
              <input
                type="search"
                value={channelQuery}
                onChange={(e) => setChannelQuery(e.target.value)}
                placeholder="Поиск по имени или номеру канала…"
                className={`w-full rounded-xl ${schoolSurface.tile} py-2.5 pl-10 pr-3 text-sm text-white placeholder:text-muted-500 focus:outline-none focus:ring-2 focus:ring-brand-400/30`}
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <ViewModeToggle mode={viewMode} onChange={setViewMode} />
              <GhostLink onClick={loadChannels}>
                <span className="material-icons text-sm">refresh</span>
                Обновить
              </GhostLink>
            </div>
          </div>

          {channelsLoading && (
            <div className="flex h-24 items-center justify-center gap-3 text-sm text-muted-300">
              <span className="material-icons animate-spin text-brand-300">progress_activity</span>
              Загрузка каналов…
            </div>
          )}
          {channelsError && (
            <div className="rounded-xl bg-critical/10 px-4 py-3 text-sm text-critical">{channelsError}</div>
          )}
          {!channelsLoading && !channelsError && channels.length === 0 && (
            <p className="text-sm text-muted-400">
              Каналов пока нет. Подождите обновления или проверьте доступность видеорегистратора.
            </p>
          )}
          {!channelsLoading && !channelsError && channels.length > 0 && filteredChannels.length === 0 && (
            <p className="text-sm text-muted-400">Ничего не найдено по запросу «{channelQuery}».</p>
          )}

          {!channelsLoading && !channelsError && filteredChannels.length > 0 && (
            <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
              <div className="flex flex-col">
                <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-500">
                  Каналы ({filteredChannels.length}
                  {channelQuery.trim() ? ` из ${channels.length}` : ''})
                </p>
                <div className="max-h-[min(22rem,55vh)] overflow-y-auto overscroll-contain pr-1">
                  {viewMode === 'list' ? (
                    <div className="space-y-1.5">
                      {filteredChannels.map((ch) => (
                        <ChannelRow
                          key={ch.channel}
                          channel={ch}
                          selected={selectedChannel?.channel === ch.channel}
                          onSelect={setSelectedChannel}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {filteredChannels.map((ch) => (
                        <ChannelTile
                          key={ch.channel}
                          channel={ch}
                          deviceId={nvr.id}
                          selected={selectedChannel?.channel === ch.channel}
                          onSelect={setSelectedChannel}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex min-h-[22rem] flex-col gap-3 lg:h-[28rem]">
                <div className="flex items-center justify-between gap-2">
                  <p className="truncate text-[11px] font-medium uppercase tracking-[0.08em] text-muted-500">
                    Просмотр
                    {selectedChannel ? `: ${selectedChannel.name || selectedChannel.channel}` : ''}
                  </p>
                  <select
                    value={profile}
                    onChange={(e) => setProfile(e.target.value)}
                    className={`rounded-lg ${schoolSurface.tile} px-2 py-1 text-xs text-white`}
                  >
                    <option value="sub">Субпоток</option>
                    <option value="main">Основной</option>
                  </select>
                </div>
                <div className="min-h-0 flex-1">
                  {selectedChannel && nvr.video_enabled ? (
                    <HikvisionLiveVideo
                      key={`${nvr.id}-${selectedChannel.channel}-${profile}`}
                      deviceId={nvr.id}
                      videoEnabled
                      defaultChannel={String(selectedChannel.channel)}
                      profile={profile}
                      nvrMode
                      className="h-full"
                    />
                  ) : (
                    <div className={`flex h-full min-h-[12rem] items-center justify-center rounded-xl ${schoolSurface.tile} text-sm text-muted-500`}>
                      Выберите канал
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </DeviceRowCard>
  )
}

const HikvisionNvrSection = ({ objectIp, userRole }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const canManage = isAdminOrEngineer(userRole)
  const [nvrs, setNvrs] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [createError, setCreateError] = useState(null)
  const [deletingId, setDeletingId] = useState(null)

  const fetchNvrs = async () => {
    if (!objectIp) return
    setIsLoading(true)
    try {
      const data = await listDoorsForSchool(objectIp)
      setNvrs(Array.isArray(data) ? data.filter((d) => d.device_type === 'nvr') : [])
      setError(null)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Ошибка загрузки')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchNvrs()
    const interval = setInterval(fetchNvrs, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [objectIp])

  const nvr = nvrs[0] || null
  const hasNvr = Boolean(nvr)

  const handleCreate = async (data) => {
    setIsCreating(true)
    setCreateError(null)
    try {
      await createDoor(objectIp, { ...data, device_type: 'nvr' })
      setIsAddModalOpen(false)
      fetchNvrs()
    } catch (err) {
      setCreateError(err?.response?.data?.message || err?.message || 'Ошибка создания')
    } finally {
      setIsCreating(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить видеорегистратор?')) return
    setDeletingId(id)
    try {
      await deleteDoor(id)
      fetchNvrs()
    } catch (err) {
      alert(err?.response?.data?.message || 'Ошибка')
    } finally {
      setDeletingId(null)
    }
  }

  const subtitle = hasNvr
    ? [
        '1 видеорегистратор',
        nvr.status === true ? 'доступен' : nvr.status === false ? 'недоступен' : null,
        nvr.model,
        nvr.firmware,
      ]
        .filter(Boolean)
        .join(' · ')
    : 'Нет видеорегистратора'

  return (
    <SectionFrame
      eyebrow="Видеонаблюдение"
      title="Видеорегистратор"
      subtitle={subtitle}
      actions={
        canManage && !hasNvr ? (
          <AddDeviceLink onClick={() => setIsAddModalOpen(true)} />
        ) : null
      }
      navLinks={
        <NavPageLink icon="local_fire_department" onClick={() => navigate('/fire-incidents')}>
          {t('fire.title')}
        </NavPageLink>
      }
    >
      {isLoading && (
        <div className="flex h-24 items-center justify-center text-sm text-muted-300">
          <span className="material-icons mr-2 animate-spin text-brand-300">progress_activity</span>
          Загрузка устройств...
        </div>
      )}

      {!isLoading && error && (
        <div className="rounded-2xl bg-critical/10 px-5 py-4 text-sm text-critical">{error}</div>
      )}

      {!isLoading && !error && !hasNvr && (
        <EquipmentEmpty
          hint="Добавьте устройство, чтобы видеть каналы, запись и пожарную сигнализацию."
          canManage={canManage}
          onAdd={() => setIsAddModalOpen(true)}
        />
      )}

      {!isLoading && !error && hasNvr && (
        <div className="flex flex-col gap-2">
          <NvrCard
            nvr={nvr}
            objectIp={objectIp}
            canManage={canManage}
            onDelete={handleDelete}
            deletingId={deletingId}
          />
          <FireRow
            objectIp={objectIp}
            nvr={nvr}
            nvrUnavailable={nvr.enabled !== false && nvr.status === false}
            canManage={canManage}
          />
        </div>
      )}

      <AddHikvisionDeviceModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleCreate}
        isLoading={isCreating}
        error={createError}
        deviceType="nvr"
      />
    </SectionFrame>
  )
}

export default HikvisionNvrSection
